/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, usePathname, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  CommunityType,
  useRequestCommunitiesAdminCommunity,
} from "@packages/sdk";
import {
  SearchBar,
  spacing,
  Tabs,
  TabsItem,
  Text,
  useDebouncedSearchParamState,
  useErrorNotification,
  useIntersectionObserverRefState,
  useSearchParamState,
} from "@packages/ui";
import { useEffect, useMemo, useRef } from "react";

import { useRequestCommunitiesAdminUsers } from "../../../../requests/useRequestCommunitiesAdminUsers";
import { useRequestCommunitiesAdminUsersPending } from "../../../../requests/useRequestCommunitiesAdminUsersPending";
import type { MemberWithAdminFlag } from "../ParishAdminDashboardMembers";
import { ParishAdminDashboardMembers } from "../ParishAdminDashboardMembers";
import { RequestsList } from "../RequestsList";

/**
 * Community types whose admins can review pending join requests. Other types
 * (network, school, class) do not surface the Requests tab at all.
 */
const REQUESTS_ENABLED_COMMUNITY_TYPES: CommunityType[] = [
  CommunityType.parish,
  CommunityType.small_group,
  CommunityType.basic,
];

const actionsContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  padding: spacing.l,
};

const containerStyles: Interpolation = {
  paddingBottom: spacing.l,
  paddingTop: spacing.l,
  width: "100%",
  ["@media (min-width: 768px)"]: {
    width: `calc(100dvw - ${spacing.sidebar})`,
  },
};

const containerNestedViewStyles: Interpolation = {
  width: "100%",
  ["@media (min-width: 768px)"]: {
    width: `calc(100dvw - ${spacing.sidebar} - ${spacing.collapsedSidebar})`,
    paddingRight: spacing.none,
    paddingLeft: spacing.none,
  },
};

const contentStyles: Interpolation = {
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
};

const headerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
};

const headerTitleContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  justifyContent: "space-between",
  paddingBottom: spacing.m,
  paddingLeft: spacing.xl,
  paddingRight: spacing.xl,
};

const searchBarStyles: Interpolation = {
  flexGrow: 1,
};

const noResultsStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  minHeight: "200px",
  padding: spacing.xl,
};

const ELLIPSES_UNICODE = "\u2026";

export type MembersListProps = {
  baseMemberLinkUrl: string;
  communityId: number;
  communityName: string;
  communityType: CommunityType;
  isPrivate: boolean;
};

export const MembersList = ({
  baseMemberLinkUrl,
  communityId,
  communityName,
  communityType,
  isPrivate,
}: MembersListProps) => {
  const { track } = useAnalytics();
  const infiniteScrollBufferRef = useRef<HTMLTableRowElement>(null);
  const totalMembersRef = useRef<number | null>(null);
  const pathname = usePathname();
  const t = useTranslations();
  // Community privacy can be changed from any surface (web, iOS, Android) or
  // the backend, so the server-rendered `isPrivate` prop can be stale (the
  // members page fetch is cached). Re-read the live value from the same admin
  // community query the Permissions page uses, refetching on mount so the gate
  // always reflects the current backend value. Fall back to the server prop
  // only until the live value resolves so an already-correct tab doesn't
  // flicker.
  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: communityId,
    queryOptions: { refetchOnMount: "always" },
  });
  const liveIsPrivate = communityQuery.data?.is_private ?? isPrivate;
  // Pending join requests only exist for private communities (public
  // communities let anyone join immediately), so the Requests tab is gated on
  // both an eligible community type and the community being private.
  const canViewRequests =
    REQUESTS_ENABLED_COMMUNITY_TYPES.includes(communityType) && liveIsPrivate;
  const [filter, setFilter] = useSearchParamState<
    false,
    "admins" | "members" | "requests"
  >({
    key: "filter",
    defaultValue: "admins",
    multiple: false,
  });
  // Guard against ?filter=requests being set on a community type that has no
  // Requests tab (e.g. via a stale/shared URL).
  const activeFilter =
    filter === "requests" && !canViewRequests ? "admins" : filter;
  const [search, setSearch, debouncedSearch] = useDebouncedSearchParamState({
    key: "search",
    defaultValue: "",
  });

  const communityAdminsInfiniteQuery = useRequestCommunitiesAdminUsers({
    params: {
      path: { community_id: communityId },
      query: { filter_by_is_admin: "admin", search: debouncedSearch },
    },
  });
  const communityMembersInfiniteQuery = useRequestCommunitiesAdminUsers({
    params: {
      path: { community_id: communityId },
      query: { filter_by_is_admin: "member", search: debouncedSearch },
    },
  });
  const communityRequestsInfiniteQuery = useRequestCommunitiesAdminUsersPending(
    {
      communityId,
      enabled: canViewRequests,
    },
  );

  useErrorNotification({
    isError:
      communityMembersInfiniteQuery.isError ||
      communityAdminsInfiniteQuery.isError ||
      communityRequestsInfiniteQuery.isError,
  });

  const currentMembers =
    activeFilter === "admins"
      ? communityAdminsInfiniteQuery.data?.pages.flatMap((page) => page.results)
      : communityMembersInfiniteQuery.data?.pages.flatMap(
          (page) => page.results,
        );
  const currentMembersTotal = currentMembers?.length ?? 0;

  const [infiniteScrollBufferRefShowing] = useIntersectionObserverRefState({
    deps: [activeFilter, currentMembersTotal],
    ref: infiniteScrollBufferRef,
  });

  const shouldFetchNextPageCommunityAdminsInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communityAdminsInfiniteQuery.hasNextPage &&
    !communityAdminsInfiniteQuery.isFetchingNextPage;

  const shouldFetchNextPageCommunityMembersInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communityMembersInfiniteQuery.hasNextPage &&
    !communityMembersInfiniteQuery.isFetchingNextPage;

  const shouldFetchNextPage =
    activeFilter === "admins"
      ? shouldFetchNextPageCommunityAdminsInfiniteQuery
      : shouldFetchNextPageCommunityMembersInfiniteQuery;

  const fetchNextAdmins = communityAdminsInfiniteQuery.fetchNextPage;
  const fetchNextMembers = communityMembersInfiniteQuery.fetchNextPage;

  useEffect(() => {
    if (shouldFetchNextPage) {
      if (activeFilter === "admins") {
        fetchNextAdmins();
      } else {
        fetchNextMembers();
      }
    }
  }, [shouldFetchNextPage, activeFilter, fetchNextAdmins, fetchNextMembers]);

  const membersWithOnlyAdminFlag = useMemo(() => {
    if (!currentMembers) return [];

    const totalAdmins =
      communityAdminsInfiniteQuery.data?.pages?.[0]?.total || 0;
    const isOnlyAdmin = totalAdmins === 1;

    return currentMembers.map((member) => ({
      ...member,
      is_only_admin: member.is_admin && isOnlyAdmin,
    }));
  }, [currentMembers, communityAdminsInfiniteQuery.data?.pages]);

  const adminsTotal = communityAdminsInfiniteQuery.data?.pages?.[0]?.total ?? 0;
  const membersTotal =
    communityMembersInfiniteQuery.data?.pages?.[0]?.non_admin_user_ids
      ?.length ?? 0;
  const requestsTotal =
    communityRequestsInfiniteQuery.data?.pages?.[0]?.total ?? 0;
  const totalMembers = useMemo(() => {
    const adminsCount = communityAdminsInfiniteQuery.data?.pages?.[0]?.total;
    const nonAdminsCount =
      communityMembersInfiniteQuery.data?.pages?.[0]?.non_admin_user_ids
        ?.length;

    if (adminsCount !== undefined && nonAdminsCount !== undefined) {
      totalMembersRef.current = adminsCount + nonAdminsCount;
    }

    return totalMembersRef.current;
  }, [communityAdminsInfiniteQuery.data, communityMembersInfiniteQuery.data]);

  const onValueChange = (newValue: "admins" | "members" | "requests") => {
    if (newValue) setFilter(newValue);
  };

  const isInGroupsNestedView = pathname.includes("/groups/");

  useEffect(() => {
    track({
      event: HallowAnalyticsEvent.ViewedAdminDashboardScreen,
      properties: {
        community_id: communityId,
        community_name: communityName,
        community_type: communityType,
        screen_name: "members",
      },
    });
  }, [communityId]);

  return (
    <div
      css={[containerStyles, isInGroupsNestedView && containerNestedViewStyles]}
    >
      <header css={headerStyles}>
        <div css={headerTitleContainerStyles}>
          <Text as="h1" type="headline" size="m">
            <I18nMessage message="community_settings_members" />
          </Text>
          {totalMembers && (
            <Text>
              <I18nMessage
                message="total_members_specified"
                values={{
                  total_members_specified: totalMembers,
                }}
              />
            </Text>
          )}
        </div>
        <Tabs onValueChange={onValueChange} type="single" value={activeFilter}>
          <TabsItem
            parentState={activeFilter}
            value="admins"
            badge={
              communityAdminsInfiniteQuery.isPending ||
              communityAdminsInfiniteQuery.isLoading
                ? ELLIPSES_UNICODE
                : adminsTotal
            }
          >
            <I18nMessage message="community_members_list_admins_section_title" />
          </TabsItem>
          <TabsItem
            parentState={activeFilter}
            value="members"
            badge={
              communityMembersInfiniteQuery.isPending ||
              communityMembersInfiniteQuery.isLoading
                ? ELLIPSES_UNICODE
                : membersTotal
            }
          >
            <I18nMessage message="community_settings_members" />
          </TabsItem>
          {canViewRequests && (
            <TabsItem
              parentState={activeFilter}
              value="requests"
              badge={
                communityRequestsInfiniteQuery.isPending ||
                communityRequestsInfiniteQuery.isLoading
                  ? ELLIPSES_UNICODE
                  : requestsTotal
              }
            >
              <I18nMessage message="community_members_requests_section_title" />
            </TabsItem>
          )}
        </Tabs>
      </header>
      {activeFilter === "requests" && canViewRequests ? (
        <div css={contentStyles}>
          <RequestsList
            communityId={communityId}
            communityType={communityType}
          />
        </div>
      ) : (
        <>
          <div css={actionsContainerStyles}>
            <SearchBar
              placeholder={t("web_search_placeholder_short")}
              stateSetter={setSearch}
              containerCSS={[searchBarStyles]}
              value={search}
            />
          </div>
          <div css={contentStyles}>
            {communityAdminsInfiniteQuery.isLoading ||
            communityMembersInfiniteQuery.isLoading ? (
              <ParishAdminDashboardMembers
                communityId={communityId}
                communityType={communityType}
                members={[]}
                baseMemberLinkUrl={baseMemberLinkUrl}
                loading={true}
                loadingSkeletonsTotal={5}
              />
            ) : membersWithOnlyAdminFlag?.length === 0 &&
              (search.length > 0 || !communityAdminsInfiniteQuery.isLoading) ? (
              <div css={noResultsStyles}>
                <Text as="p" type="body" size="m">
                  <I18nMessage message="search_no_results" />
                </Text>
              </div>
            ) : (
              <ParishAdminDashboardMembers
                communityId={communityId}
                communityType={communityType}
                members={membersWithOnlyAdminFlag as MemberWithAdminFlag[]}
                baseMemberLinkUrl={baseMemberLinkUrl}
                hasMore={
                  activeFilter === "admins"
                    ? communityAdminsInfiniteQuery.hasNextPage
                    : communityMembersInfiniteQuery.hasNextPage
                }
                infiniteScrollBufferRef={infiniteScrollBufferRef}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
